import { AbsenceListDto, ResourceDto, ResourceType } from '@lmt/shared-types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, MouseEventHandler, useMemo } from 'react';
import styles from './AbsenceCalendarAppointment.module.scss';
import { Appointment } from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import { useSortedResources } from '../../api/custom-hooks';

interface ResourceGroupProps {
  title: string;
  resources: ResourceDto[];
}

export interface AbsenceProps {
  isOtherMonth?: boolean;
  onAbsenceMouseEnter?: (
    target: HTMLElement,
    absence: AbsenceListDto[],
  ) => void;
  onAbsenceMouseLeave?: () => void;
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  data: {
    appointmentData: Appointment & {
      data: AbsenceListDto[];
    };
  };
}

export const AbsenceCalendarAppointment: FC<AbsenceProps> = (props) => {
  const absenceData = props.data?.appointmentData?.data ?? [];
  const i18next = useScopedTranslation('absences');
  const forms = useGlobalForms();
  const claims = useClaims();

  const resourceIds = useMemo(() => {
    return absenceData ? [...absenceData.map((x) => x.resourceIds)].flat() : [];
  }, [absenceData]);
  const places = useSortedResources(ResourceType.Places, true, resourceIds);
  const devices = useSortedResources(
    ResourceType.SpecialConditions,
    true,
    resourceIds,
  );
  const doctors = useSortedResources(ResourceType.Doctors, true, resourceIds);

  return (
    <div
      data-other-month={props.isOtherMonth ?? false}
      className={styles.container}
      onDoubleClick={
        !claims.permissions.manageAbsences || absenceData.length > 1
          ? undefined
          : () => {
              forms.openAbsenceEditing(absenceData[0].id);
            }
      }
      onMouseEnter={(e) => {
        const target = e.currentTarget.parentElement;
        if (!target) {
          return;
        }
        props.onAbsenceMouseEnter?.(target, absenceData);
      }}
      onMouseLeave={props.onMouseLeave}
      onMouseOver={props.onMouseOver}
    >
      <div className={styles.absenceInfo}>
        <ResourceGroup
          title={i18next.t('doctor_unavailable')}
          resources={doctors}
        />
        <ResourceGroup
          title={i18next.t('resource_unavailable')}
          resources={[...places, ...devices]}
        />
      </div>
    </div>
  );
};

const ResourceGroup: FC<ResourceGroupProps> = (props) => {
  if (!props.resources.length) {
    return null;
  }
  return (
    <div
      className={styles.resourceGroup}
      data-test-id="resource-unavailability-in-schedule-table"
    >
      <span className={styles.title}>{props.title}</span>
      {props.resources.map((resource) => (
        <span key={resource.id} className={styles.resourceTitle}>
          {resource.name}
        </span>
      ))}
    </div>
  );
};
